import React, { useState } from "react";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";
import { clearSentryUser } from "../observability/sentry";

/**
 * Change-password dialog, opened from the nav's account area.
 *
 * The backend endpoint (POST /auth/change-password) bumps
 * password_changed_at on success, which invalidates EVERY token issued
 * before the change — including this session's. So on success we do a
 * purely local sign-out (clear tokens, clear Sentry scope, hard redirect
 * to /login) rather than authStore.logout(): the normal logout would POST
 * /auth/logout with an already-dead token and detour through a doomed
 * refresh attempt in the 401 interceptor.
 */
export default function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (next !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    if (next === current) {
      setError("New password must be different from the current one.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setDone(true);
      // Give the success message a beat to render, then sign out locally
      // and land on /login (full page load resets all in-memory state).
      setTimeout(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        clearSentryUser();
        window.location.href = "/login";
      }, 1800);
    } catch (err) {
      // Unlike most endpoints (whose error field is a machine code),
      // /auth/change-password returns crafted user-facing sentences on
      // 400 (password policy) and 401 (wrong current password — final
      // by the time we see it; the interceptor's refresh-and-retry
      // already ran). Show those verbatim; anything else goes through
      // the code-mapping helper.
      const status = err?.response?.status;
      const message = err?.response?.data?.error;
      if ((status === 400 || status === 401) && typeof message === "string") {
        setError(message);
      } else {
        setError(errorToUserMessage(err, "Failed to change password."));
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-sm p-6">
        {done ? (
          <div className="text-center space-y-2 py-4">
            <div className="text-green-600 text-2xl">✓</div>
            <p className="text-sm font-medium text-gray-900">Password changed.</p>
            <p className="text-xs text-gray-500">
              All sessions have been signed out — taking you to the login page…
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Change password</h2>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Current password</label>
              <input
                type="password"
                autoComplete="current-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New password</label>
              <input
                type="password"
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={12}
              />
              <p className="mt-1 text-xs text-gray-400">
                At least 12 characters, mixing cases, digits, or symbols.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Confirm new password</label>
              <input
                type="password"
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={12}
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">{error}</p>
            )}
            <p className="text-xs text-gray-500">
              Changing your password signs you out everywhere, including this session.
            </p>
            <div className="flex justify-end space-x-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? "Changing…" : "Change password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
